import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TAXONOMY } from "../config/taxonomy.js";
import { CategoryArtTile } from "./CategoryArt.jsx";

// ─── CATEGORY MEGA-MENU (top dropdown) ───────────────────────────────────────
// "Shop by category" drops down from the header and covers about half the screen. Umbrella → sub.
// Phone: a grid of illustrated category tiles; tapping one shows its sub-categories underneath.
// Desktop: the category list on the left, the hovered category's sub-categories on the right.
// The iOS app mirrors this tree (src/config/taxonomy.js is the shared source of truth).
const NAV_H = 56;   // the fixed header's height — the panel hangs from its bottom edge

export function CategoryDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);   // umbrella slug whose subs are showing
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.innerWidth >= 760);

  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 760);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  // Each time it opens: desktop starts on the first category, phone starts with just the tiles.
  useEffect(() => { if (open) setActive(wide ? TAXONOMY[0]?.slug : null); /* eslint-disable-next-line */ }, [open]);

  // Lock body scroll while it's open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const go = (path) => { onClose(); navigate(path); };
  const cat = TAXONOMY.find((c) => c.slug === active) || null;

  const subs = cat && (
    <div>
      <button onClick={() => go(`/category/${cat.slug}`)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: "6px 0", marginBottom: 6, cursor: "pointer", fontSize: wide ? 18 : 16, fontWeight: 800, color: "var(--ink, #1a1a1a)", minHeight: 44 }}>
        Shop all {cat.label}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
      </button>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {cat.subs.map((s) => (
          <button key={s.slug} onClick={() => go(`/category/${cat.slug}/${s.slug}`)}
            style={{ background: "var(--white, #fff)", border: "1.5px solid var(--border-mid, rgba(0,0,0,0.12))", borderRadius: 4, padding: "0 14px", minHeight: 44, fontSize: 13.5, fontWeight: 600, color: "var(--ink, #1a1a1a)", cursor: "pointer" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2F63DD"; e.currentTarget.style.color = "#2F63DD"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-mid, rgba(0,0,0,0.12))"; e.currentTarget.style.color = "var(--ink, #1a1a1a)"; }}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Dim the page under the header */}
      <div onClick={onClose}
        style={{ position: "fixed", top: NAV_H, left: 0, right: 0, bottom: 0, zIndex: 1000, background: "rgba(20,12,18,0.45)", opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none", transition: "opacity 0.22s ease" }} />
      {/* Dropdown */}
      <div role="dialog" aria-label="Shop by category" aria-hidden={!open}
        style={{
          position: "fixed", top: NAV_H, left: 0, right: 0, zIndex: 1001,
          height: wide ? "min(60vh, 520px)" : "min(52vh, 520px)", display: "flex", flexDirection: "column",
          background: "var(--offwhite, #F7F2EE)", borderBottom: "1px solid var(--border, rgba(0,0,0,0.08))",
          boxShadow: "0 18px 40px rgba(0,0,0,0.18)",
          transform: open ? "translateY(0)" : "translateY(-14px)", opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden", pointerEvents: open ? "auto" : "none",
          transition: "transform 0.24s cubic-bezier(0.22,0.61,0.36,1), opacity 0.2s ease, visibility 0s linear " + (open ? "0s" : "0.24s"),
        }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: wide ? "16px 28px 20px" : "14px 14px 20px" }}>
            {wide ? (
              <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 36 }}>
                <div role="tablist" aria-label="Categories" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {TAXONOMY.map((c) => {
                    const on = c.slug === active;
                    return (
                      <button key={c.slug} role="tab" aria-selected={on}
                        onMouseEnter={() => setActive(c.slug)} onFocus={() => setActive(c.slug)}
                        onClick={() => go(`/category/${c.slug}`)}
                        style={{ display: "flex", alignItems: "center", gap: 12, textAlign: "left", background: on ? "var(--white, #fff)" : "transparent", border: "none", borderRadius: 4, padding: "4px 10px", minHeight: 44, cursor: "pointer", boxShadow: on ? "0 2px 10px rgba(0,0,0,0.06)" : "none", transition: "background 0.12s" }}>
                        <CategoryArtTile slug={c.slug} icon={c.icon} iconSize={18} style={{ width: 32, height: 32, borderRadius: 4, flexShrink: 0 }} />
                        <span style={{ fontSize: 15, fontWeight: on ? 800 : 600, color: "var(--ink, #1a1a1a)" }}>{c.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ paddingTop: 4 }}>{subs}</div>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {TAXONOMY.map((c) => {
                    const on = c.slug === active;
                    return (
                      <button key={c.slug} aria-pressed={on} onClick={() => setActive(on ? null : c.slug)}
                        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: on ? "var(--white, #fff)" : "transparent", border: `1.5px solid ${on ? "var(--ink, #1a1a1a)" : "transparent"}`, borderRadius: 6, padding: "8px 4px 8px", minHeight: 44, cursor: "pointer" }}>
                        <CategoryArtTile slug={c.slug} icon={c.icon} iconSize={22} style={{ width: "100%", height: 48, borderRadius: 4 }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink, #1a1a1a)", lineHeight: 1.2, textAlign: "center" }}>{c.label}</span>
                      </button>
                    );
                  })}
                </div>
                {cat && <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border, rgba(0,0,0,0.08))" }}>{subs}</div>}
              </>
            )}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close"
          style={{ position: "absolute", top: 8, right: 10, width: 44, height: 44, borderRadius: "50%", border: "none", background: "transparent", cursor: "pointer", fontSize: 22, lineHeight: 1, color: "var(--ink, #1a1a1a)", display: wide ? "flex" : "none", alignItems: "center", justifyContent: "center" }}>×</button>
      </div>
    </>
  );
}
